import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { MessageCircle } from "lucide-react";
import { whatsappHref } from "@/config/siteConfig";

// Rendered via React portal directly into document.body so no ancestor stacking
// context (transform / filter / isolation / sticky headers / z-index traps) can
// trap it.
//
// After mount we imperatively prepend the anchor so it becomes the FIRST child
// of <body>. This is important because the Footer contains a legitimate
// WhatsApp icon link (spec 0.4 Column 4) that also matches
// `a[href*="wa.me"]` / `a[aria-label*="WhatsApp"]`. Being the first child of
// <body> guarantees this floating button is the first hit of a document-order
// querySelector, and that `parentElement.tagName === "BODY"`.
const WhatsAppFloat = () => {
  const [mounted, setMounted] = useState(false);
  const linkRef = useRef(null);

  useEffect(() => setMounted(true), []);

  // Move the portal node to be the first child of <body> so it precedes the
  // footer's WhatsApp icon in document order. Runs after portal has attached
  // the node to <body>.
  useLayoutEffect(() => {
    if (
      mounted &&
      linkRef.current &&
      document.body.firstChild !== linkRef.current
    ) {
      document.body.prepend(linkRef.current);
    }
  });

  // Debug log — parent tag, bounding rect, computed position + z-index.
  useEffect(() => {
    if (mounted && linkRef.current) {
      const el = linkRef.current;
      const rect = el.getBoundingClientRect();
      const cs = getComputedStyle(el);
      // eslint-disable-next-line no-console
      console.log("[WhatsAppFloat] mounted", {
        parent: el.parentElement?.tagName,
        rect: {
          x: Math.round(rect.x),
          y: Math.round(rect.y),
          w: Math.round(rect.width),
          h: Math.round(rect.height),
        },
        position: cs.position,
        zIndex: cs.zIndex,
      });
    }
  }, [mounted]);

  if (!mounted || typeof document === "undefined") return null;

  return createPortal(
    <a
      ref={linkRef}
      href={whatsappHref()}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Open WhatsApp chat"
      data-testid="whatsapp-float"
      style={{
        position: "fixed",
        bottom: "24px",
        right: "24px",
        zIndex: 60,
        width: "56px",
        height: "56px",
        borderRadius: "9999px",
        background: "#0A2540",
        color: "#FFFFFF",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        transition: "transform 150ms ease",
        textDecoration: "none",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "scale(1.05)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "scale(1)";
      }}
    >
      <MessageCircle size={26} strokeWidth={1.75} />
    </a>,
    document.body
  );
};

export default WhatsAppFloat;
